import { useState, useMemo } from "@wordpress/element";
import { useSearchQueries } from "@/hooks/use-search-queries";
import { css } from "@/utils/css";
import { __ } from "@wordpress/i18n";
import { Spinner } from "@wordpress/components";
import { format, formatDistanceToNow, parseISO } from "date-fns";
import { FaTrash, FaThumbsUp, FaThumbsDown, FaQuestion, FaSearch, FaClock, FaRobot, FaInfoCircle, FaChevronLeft, FaChevronRight, FaFilter } from "react-icons/fa";
import { api } from "@/config/api-fetch";
import { toast } from "react-toastify";
import { useModal } from "@/hooks/use-modal";

type FilterType = "all" | "helpful" | "unhelpful" | "no-vote";

// Helper to parse date from backend (assumes UTC if no timezone specified)
const parseDate = (dateString: string): Date => {
    // If the date string doesn't have timezone info, treat it as UTC
    if (!dateString.includes('Z') && !dateString.includes('+') && !dateString.match(/\-\d{2}:\d{2}$/)) {
        return parseISO(dateString + 'Z');
    }
    return parseISO(dateString);
};

export function SearchQueries() {
    const [page, setPage] = useState(1);
    const { isLoading, data, refetch } = useSearchQueries(page, 20);
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [deleting, setDeleting] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [filter, setFilter] = useState<FilterType>("all");
    const { confirm } = useModal();

    const handleDeleteQuery = async (id: number) => {
        const confirmed = await confirm(__("Are you sure you want to delete this search query?", "antimanual"));

        if (confirmed) {
            setDeleting(true);
            try {
                await api.delete(`/search-queries/${id}`);
                await refetch();
                setSelectedId(null);
                toast.success(__("Search query deleted successfully", "antimanual"));
            } catch (error) {
                toast.error(__("Failed to delete search query", "antimanual"));
            }
            setDeleting(false);
        }
    };

    const queries = data?.queries || [];

    // Filter queries based on search term and filter type
    const filteredQueries = useMemo(() => {
        return queries.filter((q) => {
            const matchesSearch = searchTerm === "" || 
                q.query.toLowerCase().includes(searchTerm.toLowerCase()) ||
                q.answer.toLowerCase().includes(searchTerm.toLowerCase());
            
            const matchesFilter = filter === "all" ||
                (filter === "helpful" && q.is_helpful === true) ||
                (filter === "unhelpful" && q.is_helpful === false) ||
                (filter === "no-vote" && q.is_helpful === null);
            
            return matchesSearch && matchesFilter;
        });
    }, [queries, searchTerm, filter]);

    const selectedQuery = filteredQueries?.find((q) => q.id === selectedId) || filteredQueries?.[0];

    const getVoteStatus = (isHelpful: boolean | null) => {
        if (isHelpful === null) return { icon: FaQuestion, text: __("No feedback", "antimanual"), class: "neutral" };
        if (isHelpful) return { icon: FaThumbsUp, text: __("Helpful", "antimanual"), class: "helpful" };
        return { icon: FaThumbsDown, text: __("Not helpful", "antimanual"), class: "unhelpful" };
    };

    const filterCounts = useMemo(() => ({
        all: queries.length,
        helpful: queries.filter(q => q.is_helpful === true).length,
        unhelpful: queries.filter(q => q.is_helpful === false).length,
        "no-vote": queries.filter(q => q.is_helpful === null).length,
    }), [queries]);

    return (
        <div className={css("search-queries-enhanced")}>
            {isLoading ? (
                <div className={css("sq-loading")}>
                    <div className={css("sq-loading-spinner")}>
                        <Spinner />
                    </div>
                    <h4>{__("Loading search queries...", "antimanual")}</h4>
                    <p>{__("Please wait while we fetch your data", "antimanual")}</p>
                </div>
            ) : queries.length === 0 ? (
                <div className={css("sq-empty-state")}>
                    <div className={css("sq-empty-icon")}>
                        <FaSearch size={32} />
                    </div>
                    <h3>{__("No search queries yet", "antimanual")}</h3>
                    <p>{__("When users start searching on your site, their queries will appear here for you to review and analyze.", "antimanual")}</p>
                </div>
            ) : (
                <div className={css("sq-container")}>
                    {/* Left Panel - Query List */}
                    <div className={css("sq-list-panel")}>
                        {/* Search & Filter Header */}
                        <div className={css("sq-list-header")}>
                            <div className={css("sq-search-box")}>
                                <FaSearch className={css("sq-search-icon")} />
                                <input
                                    type="text"
                                    placeholder={__("Search queries...", "antimanual")}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className={css("sq-search-input")}
                                />
                            </div>
                            <div className={css("sq-filter-pills")}>
                                {(["all", "helpful", "unhelpful", "no-vote"] as FilterType[]).map((f) => (
                                    <button
                                        key={f}
                                        onClick={() => setFilter(f)}
                                        className={css("sq-filter-pill", filter === f && "active", f)}
                                    >
                                        {f === "all" && __("All", "antimanual")}
                                        {f === "helpful" && <><FaThumbsUp size={10} /> {__("Helpful", "antimanual")}</>}
                                        {f === "unhelpful" && <><FaThumbsDown size={10} /> {__("Not Helpful", "antimanual")}</>}
                                        {f === "no-vote" && <><FaQuestion size={10} /> {__("No Vote", "antimanual")}</>}
                                        <span className={css("sq-filter-count")}>{filterCounts[f]}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Query List */}
                        <div className={css("sq-list")}>
                            {filteredQueries.length === 0 ? (
                                <div className={css("sq-no-results")}>
                                    <FaFilter size={24} />
                                    <p>{__("No queries match your filters", "antimanual")}</p>
                                </div>
                            ) : (
                                filteredQueries.map((q) => {
                                    const isSelected = selectedQuery?.id === q.id;
                                    const voteStatus = getVoteStatus(q.is_helpful);
                                    const VoteIcon = voteStatus.icon;

                                    return (
                                        <button
                                            key={q.id}
                                            className={css("sq-list-item", isSelected && "selected")}
                                            onClick={() => setSelectedId(q.id)}
                                        >
                                            <div className={css("sq-item-vote", voteStatus.class)}>
                                                <VoteIcon size={12} />
                                            </div>
                                            <div className={css("sq-item-content")}>
                                                <div className={css("sq-item-query")}>{q.query}</div>
                                                <div className={css("sq-item-meta")}>
                                                    <FaClock size={10} />
                                                    <span>{formatDistanceToNow(parseDate(q.created_at), { addSuffix: true })}</span>
                                                </div>
                                            </div>
                                            {isSelected && (
                                                <div className={css("sq-item-indicator")} />
                                            )}
                                        </button>
                                    );
                                })
                            )}
                        </div>

                        {/* Pagination */}
                        {data && data.total > data.per_page && (
                            <div className={css("sq-pagination")}>
                                <button
                                    onClick={() => setPage(page - 1)}
                                    disabled={page === 1}
                                    className={css("sq-page-btn")}
                                >
                                    <FaChevronLeft size={12} />
                                </button>
                                <span className={css("sq-page-info")}>
                                    {page} / {Math.ceil(data.total / data.per_page)}
                                </span>
                                <button
                                    onClick={() => setPage(page + 1)}
                                    disabled={page >= Math.ceil(data.total / data.per_page)}
                                    className={css("sq-page-btn")}
                                >
                                    <FaChevronRight size={12} />
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Right Panel - Query Detail */}
                    {selectedQuery ? (
                        <div className={css("sq-detail-panel")}>
                            {/* Actions Bar */}
                            <div className={css("sq-actions-bar")}>
                                <div className={css("sq-meta-item")}>
                                    <FaClock size={12} />
                                    <span>{format(parseDate(selectedQuery.created_at), "PPPP 'at' p")}</span>
                                </div>
                                <div className={css("sq-actions-right")}>
                                    <div className={css("sq-meta-badge", getVoteStatus(selectedQuery.is_helpful).class)}>
                                        {(() => {
                                            const status = getVoteStatus(selectedQuery.is_helpful);
                                            const Icon = status.icon;
                                            return <><Icon size={12} /> {status.text}</>;
                                        })()}
                                    </div>
                                    <button
                                        className={css("sq-delete-btn")}
                                        onClick={() => handleDeleteQuery(selectedQuery.id)}
                                        disabled={deleting}
                                        title={__("Delete this query", "antimanual")}
                                    >
                                        {deleting ? <Spinner /> : <FaTrash size={14} />}
                                    </button>
                                </div>
                            </div>

                            {/* Conversation View */}
                            <div className={css("sq-conversation")}>
                                {/* User Query Card */}
                                <div className={css("sq-query-card")}>
                                    <div className={css("sq-card-header")}>
                                        <FaSearch size={14} />
                                        <span>{__("User Query", "antimanual")}</span>
                                    </div>
                                    <div className={css("sq-card-content")}>
                                        <p className={css("sq-query-text")}>{selectedQuery.query}</p>
                                    </div>
                                </div>

                                {/* AI Response Card */}
                                <div className={css("sq-response-card")}>
                                    <div className={css("sq-card-header")}>
                                        <FaRobot size={14} />
                                        <span>{__("AI Generated Response", "antimanual")}</span>
                                    </div>
                                    <div className={css("sq-card-content")}>
                                        <div 
                                            className={css("sq-response-text")}
                                            dangerouslySetInnerHTML={{ __html: selectedQuery.answer }}
                                        />
                                    </div>
                                </div>

                                {/* Context Info */}
                                <div className={css("sq-context-info")}>
                                    <div className={css("sq-context-header")}>
                                        <FaInfoCircle size={14} />
                                        <span>{__("Context Information", "antimanual")}</span>
                                    </div>
                                    <p>
                                        {__("This response was generated based on the knowledge base content available at the time of the query.", "antimanual")}
                                    </p>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className={css("sq-detail-empty")}>
                            <FaSearch size={40} />
                            <p>{__("Select a query to view details", "antimanual")}</p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
